import React, { useState, useEffect } from "react";
import { Link } from "react-scroll";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaGithub, FaLinkedin, FaEnvelope, FaCode,
  FaArrowDown, FaAward, FaReact, FaNodeJs, FaJava,
  FaPython, FaHtml5, FaCss3Alt, FaPhp, FaVuejs,
  FaBootstrap, FaLinux, FaFigma, FaWordpress, FaUnity, FaGitAlt,
  FaBars, FaTimes
} from "react-icons/fa";
import { 
  SiSpringboot, SiMongodb, SiPostgresql, SiMysql, SiNextdotjs, 
  SiTailwindcss, SiFlutter, SiPostman, SiBlender, SiArduino, 
  SiFirebase 
} from "react-icons/si";

// ============ DATA ============
const projects = [
  { title: "ConstruxFlow", desc: "Supply Chain and Inventory Management System for procurement, supplier management, inventory tracking, and payment workflows. Achieved 25-30% gain in system responsiveness.", tags: ["React", "Spring Boot", "PostgreSQL", "REST APIs", "JPA"], img: "/2.png", github: "https://github.com/ConstruxFlow", live: "https://construxflow-demo.netlify.app", date: "May – Oct 2025", category: "Full-Stack" },
  { title: "FutureFinder", desc: "Higher Education and Career Guidance Platform connecting students, institutions, and employers with Z-score analytics.", tags: ["PHP", "JavaScript", "HTML", "CSS", "MySQL"], img: "/3.png", github: "https://github.com/Imandividara/FutureFinder", live: "https://futurefinder-demo.netlify.app", date: "May 2024 – Apr 2025", category: "Full-Stack" },
  { title: "SIMAS AUTOQUEST", desc: "Car Rental Platform with location-based search, booking management, and secure online payments.", tags: ["MongoDB", "Express", "React", "Node.js"], img: "/4.png", github: "https://github.com/Imandividara/Car-Rental-System", live: "https://simas-demo.netlify.app", date: "May 2024 – Oct 2024", category: "Full-Stack" },
  { title: "FoodieHub", desc: "Online Food Ordering Platform with Stripe payment integration and optimized React components.", tags: ["Spring Boot", "React", "Tailwind CSS", "MySQL", "Stripe"], img: "/5.png", github: "https://github.com/Imandividara/FoodieHub", live: "https://foodiehub-demo.netlify.app", date: "Jul 2024 – Jan 2025", category: "Full-Stack" },
  { title: "Maze Of Shadows", desc: "3D Horror Maze Game with immersive gameplay mechanics, enemy AI with chase systems, health management, and atmospheric sound design built in Unity.", tags: ["Unity", "C#", "Game Development", "3D Modeling", "AI Systems"], img: "/6.png", github: "https://github.com/Imandividara/Maze-Of-Shadows", live: "https://foodiehub-demo.netlify.app", date: "May – Sep 2025", category: "Game Development" },
  { title: "Travel Treasures", desc: "Tourism Landing Page featuring dynamic greeting messages, time-based content, beautiful destination showcases with responsive design and modern UI/UX.", tags: ["React", "JavaScript", "CSS", "Responsive Design"], img: "/7.png", github: "https://github.com/Imandividara/Travel-Treasures", live: "https://travel-treasures-demo.netlify.app", date: "May – Jun 2024", category: "Frontend" },
];

const certificates = [
  { title: "Machine Learning Specialization", issuer: "DeepLearning.AI & Stanford Online", date: "Ongoing", status: "In Progress" },
  { title: "Unity Essentials Pathway", issuer: "Unity", date: "2025" },
  { title: "AWS Educate Introduction to Cloud 101", issuer: "Amazon Web Services", date: "2024" },
];

const skillCategories = [
  {
    title: "Programming Languages",
    items: [
      { name: "C++/C", icon: FaCode, color: "#00599C" },
      { name: "Java", icon: FaJava, color: "#007396" },
      { name: "JavaScript", icon: FaCode, color: "#F7DF1E" },
      { name: "HTML", icon: FaHtml5, color: "#E34F26" },
      { name: "CSS", icon: FaCss3Alt, color: "#1572B6" },
      { name: "Dart", icon: FaCode, color: "#00B4AB" },
      { name: "PHP", icon: FaPhp, color: "#777BB4" },
      { name: "SQL", icon: SiMysql, color: "#4479A1" },
      { name: "Python", icon: FaPython, color: "#3776AB" },
      { name: "Scala", icon: FaCode, color: "#DC322F" },
      { name: "R", icon: FaCode, color: "#276DC3" },
    ]
  },
  {
    title: "Frameworks / Libraries",
    items: [
      { name: "React.js", icon: FaReact, color: "#61DAFB" },
      { name: "Next.js", icon: SiNextdotjs, color: "#FFFFFF" },
      { name: "Vue.js", icon: FaVuejs, color: "#4FC08D" },
      { name: "React Native", icon: FaReact, color: "#61DAFB" },
      { name: "Tailwind CSS", icon: SiTailwindcss, color: "#06B6D4" },
      { name: "Bootstrap", icon: FaBootstrap, color: "#7952B3" },
      { name: "Spring Boot", icon: SiSpringboot, color: "#6DB33F" },
      { name: "Node.js", icon: FaNodeJs, color: "#339933" },
      { name: "Flutter", icon: SiFlutter, color: "#02569B" },
    ]
  },
  {
    title: "Developer Tools & Platforms",
    items: [
      { name: "Git", icon: FaGitAlt, color: "#F05032" },
      { name: "GitHub Workflows", icon: FaGithub, color: "#FFFFFF" },
      { name: "Postman", icon: SiPostman, color: "#FF6C37" },
      { name: "Linux", icon: FaLinux, color: "#FCC624" },
      { name: "Figma", icon: FaFigma, color: "#F24E1E" },
      { name: "Wordpress", icon: FaWordpress, color: "#21759B" },
      { name: "Unity", icon: FaUnity, color: "#FFFFFF" },
      { name: "Blender", icon: SiBlender, color: "#F5792A" },
      { name: "Arduino IDE", icon: SiArduino, color: "#00979D" },
      { name: "REST APIs", icon: FaCode, color: "#009688" },
      { name: "MVC", icon: FaCode, color: "#607D8B" },
    ]
  },
  {
    title: "Databases",
    items: [
      { name: "MongoDB", icon: SiMongodb, color: "#47A248" },
      { name: "MySQL", icon: SiMysql, color: "#4479A1" },
      { name: "PostgreSQL", icon: SiPostgresql, color: "#336791" },
      { name: "Firebase", icon: SiFirebase, color: "#FFCA28" },
    ]
  }
];

const heroRoles = ["Developer", "Designer", "Creator"];

const flatSkillsList = skillCategories.flatMap((category) => category.items);

// ============ MAIN COMPONENT ============
const Home = () => {
  const [scrolled, setScrolled] = useState(false);
  const [heroRoleIndex, setHeroRoleIndex] = useState(0);
  const [filterCategory, setFilterCategory] = useState("All");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPos = window.scrollY;
      setScrolled(scrollPos > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setHeroRoleIndex((currentIndex) => (currentIndex + 1) % heroRoles.length);
    }, 1800);

    return () => window.clearInterval(intervalId);
  }, []);

  const navLinks = ["Home", "About","Skills" , "Projects", "Contact"];
  const categories = ["All", "Full-Stack", "Frontend", "Backend"];
  const filteredProjects = filterCategory === "All" ? projects : projects.filter((p) => p.category === filterCategory);

  return (
    <main className="bg-black min-h-screen font-poppins text-white relative overflow-x-hidden">

      {/* Gradient Orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <motion.div className="absolute top-0 -left-40 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20"
          animate={{ x: [0, 100, 0], y: [0, 50, 0], scale: [1, 1.2, 1] }} transition={{ duration: 20, repeat: Infinity }} />
        <motion.div className="absolute top-0 -right-40 w-96 h-96 bg-cyan-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20"
          animate={{ x: [0, -100, 0], y: [0, 100, 0], scale: [1, 1.1, 1] }} transition={{ duration: 25, repeat: Infinity }} />
        <motion.div className="absolute -bottom-40 left-1/2 w-96 h-96 bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20"
          animate={{ x: [0, -50, 0], y: [0, -50, 0], scale: [1, 1.3, 1] }} transition={{ duration: 30, repeat: Infinity }} />
      </div>

      <aside className="fixed left-0 top-0 z-40 hidden h-screen w-20 lg:flex flex-col items-center border-r border-white/10 bg-black/25 backdrop-blur-sm">
        <div className="flex h-20 items-center justify-center">
          <motion.div className="text-3xl font-semibold text-cyan-300" whileHover={{ scale: 1.05 }}>
            IM
          </motion.div>
        </div>
        <div className="flex flex-1 flex-col items-center justify-center gap-4 text-[8px] uppercase tracking-[0.45em] text-white/55">
          {navLinks.map((link) => (
            <a
              key={link}
              href={`#${link.toLowerCase()}`}
              className="whitespace-nowrap [writing-mode:vertical-rl] rotate-180 hover:text-cyan-200 transition-colors"
            >
              {link}
            </a>
          ))}
        </div>
        <div className="my-8 h-24 w-px bg-white/25" />
        <div className="mb-10 flex flex-col items-center gap-4 text-white/80 text-[18px]">
          <a href="https://github.com/Imandividara" target="_blank" rel="noreferrer" className="hover:text-cyan-300 transition"><FaGithub /></a>
          <a href="https://www.linkedin.com/in/imandi-muthugala" target="_blank" rel="noreferrer" className="hover:text-cyan-300 transition"><FaLinkedin /></a>
          <a href="mailto:imandividaramuthugala@gmail.com" className="hover:text-cyan-300 transition"><FaEnvelope /></a>
        </div>
      </aside>

      <header className={`fixed top-0 left-0 right-0 z-30 border-b border-white/10 bg-black/70 backdrop-blur-xl lg:hidden ${scrolled ? "shadow-lg shadow-black/30" : ""}`}>
        <div className="flex items-center justify-between px-5 py-4">
          <div className="text-2xl font-semibold text-cyan-300">IM</div>
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
            className="text-cyan-300 hover:text-white transition-colors duration-200 text-xl focus:outline-none"
          >
            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>

        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="bg-black/95 border-t border-white/5 px-6 py-5 flex flex-col gap-4 text-xs uppercase tracking-[0.25em]"
            >
              {navLinks.map((link) => (
                <Link 
  to={link.toLowerCase()} 
  smooth={true} 
  duration={500} 
  offset={-70} // This offsets the fixed header height
  onClick={() => setMobileMenuOpen(false)}
  className="hover:text-cyan-300 transition-colors py-1 cursor-pointer"
>
  {link}
</Link>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* HOME / HERO SECTION */}
      <section
        id="home"
        className="relative min-h-screen overflow-hidden px-4 pt-6 pb-12 lg:px-6 lg:pl-32 lg:pt-4 lg:pb-16 bg-[#04080c] bg-top bg-cover"
        style={{
          backgroundImage:
            "linear-gradient(rgba(4,8,12,0.25), rgba(4,8,12,0.55)), url('/hero-background_image.png')",
        }}
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(16,245,245,0.10),transparent_24%),radial-gradient(circle_at_82%_18%,rgba(16,245,245,0.08),transparent_22%),radial-gradient(circle_at_50%_110%,rgba(16,245,245,0.06),transparent_28%)]" />
        <div
          className="absolute inset-0 opacity-[0.12]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />

        <div className="relative z-10 grid min-h-[calc(100vh-5rem)] grid-cols-1 items-center gap-12 px-6 py-10 lg:grid-cols-[1.05fr_0.95fr] lg:px-16 xl:px-20 lg:py-8">
            <motion.div
              className="max-w-2xl"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.7 }}
            >
              <p className="mb-4 text-[11px] md:text-xs font-medium uppercase tracking-[0.5em] text-cyan-200/75">
                I&apos;m Imandi Muthugala
              </p>
              <div className="space-y-1 md:space-y-2 xl:space-y-3">
                {heroRoles.map((role, index) => {
                  const isActive = index === heroRoleIndex;

                  return (
                    <motion.h1
                      key={role}
                      className="text-3xl font-semibold leading-[0.88] md:text-5xl xl:text-[4.1rem]"
                      animate={{
                        opacity: isActive ? 1 : 0.42,
                        color: isActive ? "#57d5e8" : index === 0 ? "#ffffff" : "#2f8c9d",
                        x: isActive ? 0 : 0,
                      }}
                      transition={{ duration: 0.45, ease: "easeOut" }}
                    >
                      <span className={index === 0 ? "text-white" : index === 1 ? "text-cyan-300" : "text-cyan-300/70"}>
                        {role}
                      </span>
                    </motion.h1>
                  );
                })}
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <motion.a
                  href="#projects"
                  className="rounded-sm border border-cyan-400/35 bg-cyan-400/8 px-6 py-3 text-[11px] font-medium uppercase tracking-[0.35em] text-cyan-100 transition hover:bg-cyan-400/12"
                  whileHover={{ y: -2, scale: 1.02 }}
                >
                  View work
                </motion.a>
                <motion.a
                  href="#contact"
                  className="rounded-sm border border-white/10 bg-white/5 px-6 py-3 text-[11px] font-medium uppercase tracking-[0.35em] text-white/70 transition hover:border-cyan-400/25 hover:text-cyan-100"
                  whileHover={{ y: -2, scale: 1.02 }}
                >
                  Contact
                </motion.a>
              </div>
            </motion.div>

            {/* Responsive Hardware Line Marquee Module Node */}
            <div className="relative flex items-center justify-center lg:justify-start lg:min-h-[560px] w-full lg:w-auto">
              
              {/* DESKTOP MODE: Automated Infinite Vertical Line Layout */}
              <div className="hidden lg:flex flex-col items-center gap-4 lg:absolute lg:right-8 lg:top-1/2 lg:-translate-y-1/2">
                <div className="h-32 w-px bg-white/20" />
                <div className="h-60 w-14 overflow-hidden rounded-full border border-cyan-300/20 bg-[#081014]/70 backdrop-blur-md flex flex-col items-center relative py-4">
                  <motion.div 
                    className="flex flex-col gap-3 items-center"
                    animate={{ y: [0, "-50%"] }}
                    transition={{ ease: "linear", duration: 30, repeat: Infinity }}
                  >
                    {[...flatSkillsList, ...flatSkillsList].map((skill, index) => {
                      const SkillIcon = skill.icon;
                      return (
                        <div
                          key={`${skill.name}-v-${index}`}
                          className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/75 shrink-0"
                          title={skill.name}
                        >
                          <SkillIcon className="text-sm" style={{ color: skill.color }} />
                        </div>
                      );
                    })}
                  </motion.div>
                </div>
              </div>

              {/* MOBILE MODE: Automated Infinite Horizontal Line Layout */}
              <div className="flex lg:hidden w-full justify-center px-4">
                <div className="w-full max-w-[280px] sm:max-w-md h-14 overflow-hidden rounded-full border border-cyan-300/20 bg-[#081014]/70 backdrop-blur-md flex items-center relative px-4">
                  <motion.div 
                    className="flex flex-row gap-3 items-center whitespace-nowrap"
                    animate={{ x: [0, "-50%"] }}
                    transition={{ ease: "linear", duration: 25, repeat: Infinity }}
                  >
                    {[...flatSkillsList, ...flatSkillsList].map((skill, index) => {
                      const SkillIcon = skill.icon;
                      return (
                        <div
                          key={`${skill.name}-h-${index}`}
                          className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white/75 shrink-0"
                          title={skill.name}
                        >
                          <SkillIcon className="text-sm" style={{ color: skill.color }} />
                        </div>
                      );
                    })}
                  </motion.div>
                </div>
              </div>

            </div>
          </div>

        <div className="absolute bottom-6 left-1/2 z-20 -translate-x-1/2">
            <motion.div
              className="flex items-center gap-3 text-[10px] uppercase tracking-[0.45em] text-white/45"
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2.2, repeat: Infinity }}
            >
              <span className="h-px w-16 bg-white/20" />
              <span>Scroll</span>
              <FaArrowDown className="text-cyan-300" />
            </motion.div>
        </div>
      </section>

      {/* ============ UNIFIED ABOUT & EXPERIENCE WALL ============ */}
      <section
        className="relative overflow-hidden bg-center bg-cover"
        style={{
          backgroundImage:
            "linear-gradient(rgba(4,8,12,0.72), rgba(4,8,12,0.82)), url('/above-background_image.png')",
        }}
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(16,245,245,0.12),transparent_28%),radial-gradient(circle_at_80%_70%,rgba(16,245,245,0.08),transparent_22%)]" />

        {/* SUB SECTION: ABOUT */}
        <div id="about" className="relative z-10 container mx-auto max-w-7xl px-6 py-20 lg:px-20 lg:pl-40">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:gap-16">
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-[10px] md:text-xs font-medium uppercase tracking-[0.55em] text-cyan-200/75">
                About
              </p>
              <h2 className="mt-3 text-4xl font-semibold leading-none text-cyan-300 md:text-5xl">
                About
              </h2>

              <div className="mt-10 flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-4 max-w-md">
                <div className="w-28 shrink-0 overflow-hidden rounded-sm border border-white/10 bg-white/5 p-2">
                  <img src="/profile.png" alt="Imandi Muthugala" className="h-28 w-full object-cover grayscale" />
                </div>
                <p className="pt-2 text-[11px] leading-6 text-white/65 md:text-xs md:leading-7">
                  I&apos;m Imandi Muthugala, a Computer Science student focused on building polished web experiences and practical full-stack systems.
                </p>
              </div>
            </motion.div>

            <motion.div
              className="space-y-12"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div>
                <h3 className="text-sm font-semibold text-white md:text-base">Education</h3>
                <div className="mt-6 space-y-6 text-[10px] uppercase tracking-[0.3em] text-white/70 md:text-[11px]">
                  <div>
                    <div className="mb-1 text-white/40">01</div>
                    <div className="text-white tracking-[0.18em]">Bachelor of Science in Computer Science</div>
                    <div className="mt-1 text-cyan-300/80 tracking-[0.22em]">Second Class Upper Division</div>
                    <div className="mt-1 text-cyan-200/70 tracking-[0.22em]">GPA: 3.475</div>
                    <div className="mt-1 text-cyan-200/70 tracking-[0.22em]">2 Director&apos;s Lists</div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-white md:text-base">Achievements & Certifications</h3>
                <div className="mt-6 space-y-6 text-[10px] uppercase tracking-[0.3em] text-white/70 md:text-[11px]">
                  {[
                    "Winner - SLIIT Codefest Designathon 2025 (IFS)",
                    "Top 50 - INTERFACE X Designathon 2025 (Asta)",
                    "Participant - IntelliHack 5.0 (UCSC)",
                  ].map((item, idx) => (
                    <div key={item}>
                      <div className="mb-1 text-white/40">0{idx + 1}</div>
                      <div className="tracking-[0.18em] text-white/85">{item}</div>
                      <div className="mt-1 text-cyan-300/70 tracking-[0.22em]">2025</div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* SUB SECTION: EXPERIENCE */}
        <div id="experience" className="relative z-10 container mx-auto max-w-7xl px-6 py-20 lg:px-20 lg:pl-40 border-t border-white/5">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:gap-16">
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-[10px] md:text-xs font-medium uppercase tracking-[0.55em] text-cyan-200/75">
                History
              </p>
              <h2 className="mt-3 text-4xl font-semibold leading-none text-cyan-300 md:text-5xl">
                Experience
              </h2>

              <div className="mt-10 flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-4 max-w-md">
                <div className="w-28 h-28 shrink-0 overflow-hidden rounded-sm border border-white/10 bg-white/5 p-2 flex items-center justify-center">
                  <img 
                    src="/effective solutions-logo.png" 
                    alt="Effective Solutions" 
                    className="max-h-full max-w-full object-contain grayscale hover:grayscale-0 transition-all duration-300" 
                  />
                </div>
                <div className="pt-1">
                  <h3 className="text-sm font-semibold text-white md:text-base tracking-[0.05em]">Effective Solutions (Pvt) Ltd</h3>
                  <p className="text-[11px] text-cyan-300/80 tracking-[0.18em] uppercase mt-1">Software Engineer Intern</p>
                  <p className="text-[10px] text-white/40 tracking-[0.15em] uppercase mt-1">17 Nov 2025 – 17 May 2026</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="space-y-8 pt-2 lg:pt-14"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div>
                <h3 className="text-sm font-semibold text-white md:text-base uppercase tracking-[0.2em] mb-4">Core Focus & Workflow</h3>
                <div className="space-y-4 text-xs leading-6 text-white/65 md:text-sm md:leading-7">
                  <p>
                    Engineered premium full-stack mobile and web applications, designing and delivering robust modular technical systems across production-level applications.
                  </p>
                  <p>
                    Thrived within structured agile sprints and highly collaborative team environments, actively participating in iterative cycles, standups, and shared codebase management to deploy high-performance applications.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* SUB SECTION: SKILLS & CERTIFICATIONS CONTENT */}
        <div id="skills" className="relative z-10 container mx-auto max-w-7xl px-6 py-20 lg:px-20 lg:pl-40 border-t border-white/5">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[0.95fr_1.05fr] lg:gap-16">
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-[10px] md:text-xs font-medium uppercase tracking-[0.55em] text-cyan-200/75">
                Capabilities
              </p>
              <h2 className="mt-3 text-4xl font-semibold leading-none text-cyan-300 md:text-5xl">
                Skills
              </h2>
              
              <div className="mt-14 border-t border-white/5 pt-8 hidden lg:block">
                <h3 className="text-xs font-semibold text-white/40 uppercase tracking-[0.2em] mb-6">Certifications</h3>
                <div className="space-y-5">
                  {certificates.map((cert, cIdx) => (
                    <div key={cIdx} className="text-xs text-white/70">
                      <div className="flex items-center gap-2 font-medium text-white/90">
                        <FaAward className="text-cyan-400 text-sm shrink-0" />
                        <span>{cert.title}</span>
                      </div>
                      <div className="text-[11px] text-white/40 mt-1 pl-5 tracking-wide">
                        {cert.issuer} • {cert.date}
                        {cert.status && <span className="ml-2 text-cyan-300/80">({cert.status})</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            <motion.div
              className="space-y-10 pt-2 lg:pt-14"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {skillCategories.map((cat, cIdx) => (
                <div key={cIdx}>
                  <h3 className="text-xs font-semibold text-white uppercase tracking-[0.25em] mb-4 text-cyan-200/40">
                    {cat.title}
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {cat.items.map((skill, sIdx) => {
                      const SkillIcon = skill.icon;
                      return (
                        <div 
                          key={sIdx}
                          className="flex items-center gap-2 px-3 py-1.5 rounded bg-white/5 border border-white/5 text-xs text-white/80 hover:border-cyan-400/30 hover:text-white hover:bg-white/10 transition-all duration-300 tracking-wide"
                        >
                          <SkillIcon style={{ color: skill.color }} className="text-xs shrink-0" />
                          <span>{skill.name}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              <div className="border-t border-white/5 pt-8 block lg:hidden">
                <h3 className="text-xs font-semibold text-white uppercase tracking-[0.25em] mb-4 text-cyan-200/40">
                  Certifications
                </h3>
                <div className="space-y-4">
                  {certificates.map((cert, cIdx) => (
                    <div key={cIdx} className="text-xs text-white/70">
                      <div className="flex items-center gap-2 font-medium">
                        <FaAward className="text-cyan-400 text-sm shrink-0" />
                        <span>{cert.title}</span>
                      </div>
                      <div className="text-[11px] text-white/40 mt-0.5 pl-5">
                        {cert.issuer} • {cert.date}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============ PROJECTS SECTION WITH BALANCED SHADOW OVERLAY ============ */}
      <section
        id="projects"
        className="relative overflow-hidden px-6 py-24 lg:px-20 lg:pl-40 bg-[#04080c] bg-center bg-cover"
        style={{
          backgroundImage:
            "linear-gradient(rgba(4,8,12,0.65), rgba(4,8,12,0.80)), url('/hero-background_image.png')",
        }}
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(16,245,245,0.10),transparent_24%),radial-gradient(circle_at_82%_18%,rgba(16,245,245,0.08),transparent_22%),radial-gradient(circle_at_50%_110%,rgba(16,245,245,0.06),transparent_28%)]" />
        <div
          className="absolute inset-0 opacity-[0.12]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />

        <div className="relative z-10 container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-12 lg:gap-16">
            
            <div>
              <div className="lg:sticky lg:top-28">
                <p className="text-[10px] md:text-xs font-medium uppercase tracking-[0.55em] text-cyan-200/75 mb-2">
                  Showcase
                </p>
                <div className="relative select-none">
                  <h2 className="text-5xl md:text-6xl font-bold tracking-tight text-cyan-400 leading-none">
                    Projects
                  </h2>
                  <h2 className="text-5xl md:text-6xl font-bold tracking-tight text-cyan-950/30 leading-none mt-1 select-none">
                    Projects
                  </h2>
                </div>

                <div className="flex flex-wrap gap-2 mt-12 max-w-xs">
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => setFilterCategory(category)}
                      className={`px-4 py-1.5 rounded text-xs font-semibold uppercase tracking-wider transition-all border ${
                        filterCategory === category
                          ? "bg-cyan-400/10 border-cyan-400 text-cyan-300 shadow-md shadow-cyan-400/5"
                          : "bg-transparent text-white/40 border-white/5 hover:border-white/10 hover:text-white"
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="space-y-14 lg:pt-8">
              <AnimatePresence mode="wait">
                <motion.div 
                  layout 
                  className="space-y-14"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  {filteredProjects.map((project, idx) => (
                    <motion.div
                      layout
                      key={project.title}
                      className="relative border-b border-white/5 pb-10 last:border-0 group"
                      initial={{ y: 30, opacity: 0 }}
                      whileInView={{ y: 0, opacity: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: idx * 0.05 }}
                    >
                      <div className="text-[11px] font-semibold text-white/30 tracking-widest uppercase mb-1">
                        0{idx + 1}
                      </div>

                      <div className="flex flex-wrap items-center justify-between gap-4">
                        <a 
                          href={project.github} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-xl md:text-2xl font-medium text-white/90 group-hover:text-cyan-300 transition-colors duration-300 tracking-wide flex items-center gap-3.5"
                        >
                          <span>{project.title}</span>
                          <FaGithub className="text-lg md:text-xl text-white/30 group-hover:text-cyan-300 transition-colors duration-300 shrink-0" />
                        </a>
                      </div>

                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-2 mb-4 text-[11px] text-white/40 font-mono">
                        <span className="text-cyan-400/60 font-semibold tracking-wider uppercase">
                          {project.date}
                        </span>
                        <span className="text-white/10">•</span>
                        <div className="flex flex-wrap gap-1.5">
                          {project.tags.map((tag, tIdx) => (
                            <span key={tIdx} className="text-white/60 bg-white/5 border border-white/5 px-2 py-0.5 rounded text-[10px]">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="text-xs md:text-sm leading-6 md:leading-7 text-white/55 max-w-2xl font-light">
                        <p className="flex items-start gap-2.5">
                          <span className="h-1.5 w-1.5 rounded-full bg-cyan-400/75 mt-2 shrink-0 block" />
                          <span>{project.desc}</span>
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </AnimatePresence>
            </div>

          </div>
        </div>
      </section>

      {/* ============ DESKTOP/MOBILE HARDWARE MOCKUP BLOCK ============ */}
      {/* <section className="py-24 px-6 bg-black flex justify-center border-t border-white/5">
        <div className="relative w-full max-w-4xl p-2 bg-gradient-to-b from-white/10 to-transparent rounded-2xl shadow-2xl">
          <video 
            src="/linkedin-demo.mp4" 
            autoPlay 
            loop 
            muted 
            playsInline 
            className="w-full rounded-xl border border-white/5 object-cover aspect-video"
          />
        </div>
      </section> */}

      {/* ============ CONTACT SECTION ============ */}
      <section
        id="contact"
        className="relative overflow-hidden px-6 py-24 lg:px-20 lg:pl-40 bg-[#04080c] bg-center bg-cover border-t border-white/5"
        style={{
          backgroundImage:
            "linear-gradient(rgba(4,8,12,0.88), rgba(4,8,12,0.95)), url('/hero-background_image.png')",
        }}
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(16,245,245,0.06),transparent_24%),radial-gradient(circle_at_82%_18%,rgba(16,245,245,0.05),transparent_22%)]" />
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
          }}
        />

        <div className="relative z-10 container mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-12 lg:gap-16">
            
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-[10px] md:text-xs font-medium uppercase tracking-[0.55em] text-cyan-200/75 mb-2">
                Connect
              </p>
              <div className="relative select-none">
                <h2 className="text-5xl md:text-6xl font-bold tracking-tight text-cyan-400 leading-none">
                  Contact
                </h2>
                <h2 className="text-5xl md:text-6xl font-bold tracking-tight text-cyan-950/30 leading-none mt-1 select-none">
                  Contact
                </h2>
              </div>
              <p className="mt-8 text-xs md:text-sm text-white/50 max-w-xs leading-6 font-light">
                Open for full-stack, frontend, or backend engineering internship opportunities. Let's create something exceptional together.
              </p>

              <div className="mt-10 flex flex-wrap gap-4">
                <motion.a
                  href="/Imandi_Muthugala_Resume.pdf"
                  download
                  className="rounded-sm border border-cyan-400/35 bg-cyan-400/8 px-5 py-3 text-[10px] font-medium uppercase tracking-[0.35em] text-cyan-100 transition hover:bg-cyan-400/12"
                  whileHover={{ y: -2, scale: 1.02 }}
                >
                  Download CV
                </motion.a>
                <motion.a
                  href="mailto:imandividaramuthugala@gmail.com"
                  className="rounded-sm border border-white/10 bg-white/5 px-5 py-3 text-[10px] font-medium uppercase tracking-[0.35em] text-white/70 transition hover:border-cyan-400/25 hover:text-cyan-100"
                  whileHover={{ y: -2, scale: 1.02 }}
                >
                  Send Email
                </motion.a>
              </div>
            </motion.div>

            <motion.div 
              className="space-y-12 lg:pt-6"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {[
                { idx: "01", label: "Email Address", value: "imandividaramuthugala@gmail.com", href: "mailto:imandividaramuthugala@gmail.com" },
                { idx: "02", label: "Phone Number", value: "+94 70 420 8782", href: "tel:+94704208782" },
                { idx: "03", label: "Work Location", value: "Colombo, Sri Lanka", href: null },
              ].map((item) => (
                <div key={item.idx} className="border-b border-white/5 pb-8 last:border-0 group">
                  <div className="text-[11px] font-semibold text-white/20 tracking-widest uppercase mb-1">
                    {item.idx}
                  </div>
                  <div className="text-[10px] font-semibold text-cyan-400/50 uppercase tracking-[0.2em] mb-2">
                    {item.label}
                  </div>
                  {item.href ? (
                    <a 
                      href={item.href}
                      className="text-lg md:text-xl font-medium text-white/90 group-hover:text-cyan-300 transition-colors duration-300 break-all tracking-wide"
                    >
                      {item.value}
                    </a>
                  ) : (
                    <p className="text-lg md:text-xl font-medium text-white/90 tracking-wide">
                      {item.value}
                    </p>
                  )}
                </div>
              ))}

              <div className="pt-4 flex gap-8">
                {[
                  { icon: FaGithub, link: "https://github.com/Imandividara", name: "GitHub" },
                  { icon: FaLinkedin, link: "https://www.linkedin.com/in/imandi-muthugala", name: "LinkedIn" },
                  { icon: FaEnvelope, link: "mailto:imandividaramuthugala@gmail.com", name: "Email" },
                ].map(({ icon: Icon, link, name }) => (
                  <motion.a
                    key={name}
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/40 hover:text-cyan-300 text-2xl transition-colors relative group"
                    whileHover={{ y: -4, scale: 1.15, rotate: 360 }}
                    whileTap={{ scale: 0.92 }}
                    transition={{ duration: 0.3 }}
                    title={name}
                  >
                    <Icon />
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-cyan-500 rounded-full blur-xl opacity-0 group-hover:opacity-40 transition-opacity" />
                  </motion.a>
                ))}
              </div>
            </motion.div>

          </div>
        </div>
      </section>
          
      <footer className="relative bg-black py-12 px-6 border-t border-white/5 z-10">
        <div className="container max-w-7xl mx-auto text-center">
          <motion.p 
            className="text-[10px] uppercase tracking-[0.35em] text-white/30" 
            initial={{ opacity: 0 }} 
            whileInView={{ opacity: 1 }} 
            viewport={{ once: true }}
          >
            © 2025 Imandi Muthugala. Crafted with{" "}
            <span className="text-white/50">pure dedication and creative passion</span>
          </motion.p>
        </div>
      </footer>
    </main>
  );
};

export default Home;